import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  Inject,
  inject,
  Input,
  OnDestroy,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  AbstractControl,
  FormBuilder,
  FormControl,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { AppConfigService } from '@core/app-config/app-config.service';
import { FlightTrackerService } from '@core/flight-tracker/flight-tracker.service';
import { WINDOW } from '@core/injection-tokens';
import { TypedDictionary } from '@core/utils';
import {
  CityPair,
  CityPairSelectorComponent,
} from '@dynamic-components/booker-v2/shared/city-pair-selector';
import {
  DateOption,
  FlightTrackerTabId,
} from '@dynamic-components/flight-tracker/flight-tracker-search/flight-tracker-search.component.state';
import { ComponentStore } from '@ngrx/component-store';
import { focusOnErrorField } from '@shared/ui/utils/form-control-utils/focus-on-error-field';
import { BookerFacade } from '@store/booker/booker.facade';
import { FlightTrackerFacade } from '@store/flight-tracker/flight-tracker.facade';
import { FlightResults } from '@store/flight-tracker/types';
import { applyFilters } from '@store/origins/api';
import { OriginsFacade } from '@store/origins/origins.facade';
import { Airport } from '@store/origins/types';
import { format } from 'date-fns';
import { JbTabsComponent } from 'jb-component-library';
import { isEqual } from 'lodash';
import { combineLatest, merge, Observable } from 'rxjs';
import {
  distinctUntilChanged,
  filter,
  first,
  map,
  startWith,
  tap,
} from 'rxjs/operators';

import { FLIGHT_TRACKER_CONFIG } from '../flight-tracker.config';
import { FlightTrackerMessages } from '../flight-tracker.type';

@Component({
  selector: 'jb-flight-tracker-search',
  templateUrl: './flight-tracker-search.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [ComponentStore],
})
export class FlightTrackerSearchComponent implements OnDestroy {
  private _destroy = inject(DestroyRef);

  FlightTrackerTabId = FlightTrackerTabId;
  @Input() messages: FlightTrackerMessages;
  @ViewChild('flightNumberInput', { read: ElementRef })
  flightNumberInput: ElementRef<HTMLInputElement>;

  @ViewChild(CityPairSelectorComponent)
  cityPairSelectorComponent: CityPairSelectorComponent;
  @ViewChild('jbTabs') jbTabs: JbTabsComponent;

  @ViewChild('byRouteFormReference') byRouteFormReference;
  showResults = true;
  readonly formWrapper: HTMLFormElement = this.elementRef.nativeElement;

  readonly flightTrackerConfig = FLIGHT_TRACKER_CONFIG;

  readonly byRouteForm = this.fb.group<{
    cityPair: CityPair;
    departureDate: string;
  }>({
    cityPair: {
      origin: null,
      destination: null,
    },
    departureDate: null,
  });

  readonly selectedRoute$: Observable<{
    origin: string;
    destination: string;
  }> = this.byRouteForm.controls.cityPair.valueChanges.pipe(
    startWith(this.byRouteForm.controls.cityPair.value),
    map(cityPair => ({
      origin: cityPair.origin?.code,
      destination: cityPair.destination?.code,
    })),
  );

  readonly flightNumberInputValidator = (
    control: AbstractControl<string>,
  ): { [key: string]: any } | null => {
    let flighttNummber = control.value;
    if (flighttNummber) {
      // remove spaces, then remove starting "b6" characters
      flighttNummber = flighttNummber.replace(/\s/g, '').replace(/^b6/i, '');
    }

    if (flighttNummber && !flighttNummber.match(/^\d{1,4}$/)) {
      return { validFlightNo: { valid: false } };
    }
    return null;
  };

  readonly byFlightNumberForm = this.fb.group<{
    flightNumber: FormControl<string>;
    departureDate: FormControl<string>;
  }>({
    flightNumber: this.fb.control<string>(null, [
      Validators.required,
      this.flightNumberInputValidator,
    ]),
    departureDate: null,
  });

  readonly isSubmitting$: Observable<boolean> =
    this.flightTrackerFacade.isFormSubmitting;

  readonly flightResults$: Observable<FlightResults> =
    this.flightTrackerFacade.getAllFlightData;

  readonly originAirportHashMap: Observable<TypedDictionary<Airport>> =
    this.flightTrackerFacade.originAirportsHashMap;

  readonly destinationAirportHashMap: Observable<TypedDictionary<Airport>> =
    this.flightTrackerFacade.destinationAirportHashMap;

  readonly dateOptions: Observable<DateOption[]> =
    this.flightTrackerFacade.dateOptions;

  readonly flightTrackerTabs: Observable<{ title: string; id: string }[]> =
    this.flightTrackerFacade.flightTrackerResponse.pipe(
      map(flightTrackerResponse => [
        {
          title: flightTrackerResponse.byRouteTab,
          id: FlightTrackerTabId.byRoute,
        },
        {
          title: flightTrackerResponse.byFlightNoTab,
          id: FlightTrackerTabId.byFlight,
        },
      ]),
    );

  readonly selectedTab: Observable<string> =
    this.flightTrackerFacade.selectedTabId.pipe(
      map(id => {
        let type: FlightTrackerTabId;
        if (id === 0) {
          type = FlightTrackerTabId.byRoute;
        } else {
          type = FlightTrackerTabId.byFlight;
        }
        return type;
      }),
    );

  readonly flightStatus: Observable<any> = combineLatest([
    this.flightTrackerFacade.flightTracker,
    this.flightTrackerTabs,
    this.selectedTab,
    this.dateOptions,
  ]).pipe(
    tap(() => {
      this.jbTabs?.calculateButtonListWidth();
    }),
    map(([status, flightTrackerTabs, selectedTabId, dateOptions]) => ({
      ...status,
      flightTrackerTabs,
      selectedTabId,
      dateOptions,
    })),
  );

  readonly today: Observable<Date> = this.flightTrackerFacade.today;

  constructor(
    private fb: FormBuilder,
    private elementRef: ElementRef,
    private route: ActivatedRoute,
    private router: Router,
    private bookerFacade: BookerFacade,
    private originsFacade: OriginsFacade,
    private flightTrackerFacade: FlightTrackerFacade,
    private flightTrackerService: FlightTrackerService,
    private appConfigService: AppConfigService,
    @Inject(WINDOW) private window,
  ) {
    this.today
      .pipe(
        first(),
        map(today => format(today, 'yyyy-MM-dd')),
      )
      .subscribe(today => {
        this.byRouteForm.patchValue({ departureDate: today });
        this.byFlightNumberForm.patchValue({ departureDate: today });
      });

    this.originsFacade.countriesWithAirports
      .pipe(
        filter(originAirports => !!originAirports?.length),
        map(originAirports => applyFilters(originAirports, true, false, [])),
        map(originAirports => ({
          originAirports,
        })),
      )
      .subscribe(originAirports =>
        this.flightTrackerFacade.setOrigins(originAirports),
      );

    combineLatest([
      this.selectedRoute$,
      this.originsFacade.destinationAirports('FLIGHT_TRACKER'),
      this.originsFacade.countriesWithAirports,
    ])
      .pipe(
        map(([selectedRoute, destinationAirports, allAirports]) =>
          !!selectedRoute.origin ? destinationAirports : allAirports,
        ),
        filter(destinationAirports => !!destinationAirports?.length),
        map(destinationAirports =>
          applyFilters(destinationAirports, true, false, []),
        ),
        map(destinationAirports => ({
          destinationAirports,
        })),
      )
      .subscribe(destinationAirports =>
        this.flightTrackerFacade.setDestinations(destinationAirports),
      );

    this.byRouteForm.controls.cityPair.valueChanges
      .pipe(
        map(cityPair => cityPair.origin?.code),
        distinctUntilChanged(),
        filter(originCode => !!originCode),
        takeUntilDestroyed(this._destroy),
      )
      .subscribe(originCode => {
        if (this.byRouteFormReference) {
          this.byRouteFormReference.submitted = false;
        }
        // clear destination if same as origin
        if (
          this.byRouteForm.controls.cityPair.value.destination?.code ===
          originCode
        ) {
          this.byRouteForm.controls.cityPair.patchValue({
            origin: this.byRouteForm.controls.cityPair.value.origin,
            destination: undefined,
          });
        }
        this.originsFacade.requestDestinationAirports(
          originCode,
          'FLIGHT_TRACKER',
        );
      });

    // when flight tracker destination map is updated check that the current destination is
    // apart of that map, otherwise remove the destination from the form
    this.originsFacade
      .destinationAirportsMap('FLIGHT_TRACKER')
      .pipe(takeUntilDestroyed(this._destroy), distinctUntilChanged(isEqual))
      .subscribe(m => {
        if (
          m &&
          !m[this.byRouteForm.controls.cityPair.value?.destination?.code]
        ) {
          this.byRouteForm.patchValue({
            cityPair: {
              origin: this.byRouteForm.controls.cityPair.value.origin,
              destination: undefined,
            },
          });
        }
      });

    this.byRouteForm.controls.cityPair.valueChanges
      .pipe(
        map(cityPair => cityPair.destination?.code),
        filter(destinationCode => !!destinationCode),
        distinctUntilChanged(),
        takeUntilDestroyed(this._destroy),
      )
      .subscribe(destinationCode =>
        this.originsFacade.requestOriginAirports(
          destinationCode,
          'FLIGHT_TRACKER',
        ),
      );

    combineLatest([
      this.route.queryParams,
      this.dateOptions,
      this.originAirportHashMap,
      this.destinationAirportHashMap,
      this.bookerFacade.currentOrigin$(true),
    ])
      .pipe(
        filter(([_, __, originAirportHashMap, destinationAirportHashMap]) => {
          return (
            Object.keys(originAirportHashMap).length > 0 &&
            Object.keys(destinationAirportHashMap).length > 0
          );
        }),
        first(),
      )
      .subscribe(
        ([
          queryParams,
          dateOptions,
          originAirportHashMap,
          destinationAirportHashMap,
          currentOrigin,
        ]) =>
          this.prePopulateBooker(
            queryParams,
            dateOptions,
            originAirportHashMap,
            destinationAirportHashMap,
            currentOrigin,
          ),
      );

    merge(
      this.byRouteForm.valueChanges,
      this.byFlightNumberForm.valueChanges,
      this.selectedTab,
    )
      .pipe(takeUntilDestroyed(this._destroy))
      .subscribe(() => this.flightTrackerFacade.setFlightStatus(undefined));
  }
  getSearchResults(type: FlightTrackerTabId) {
    this.showResults = true;
    if (type === FlightTrackerTabId.byFlight) {
      this.getSearchResultsByFlight();
    } else {
      this.getSearchResultsByRoute();
    }
  }

  getSearchResultsByRoute(): void {
    const { departureDate, cityPair } = this.byRouteForm.value;
    this.setQueryParams({
      by: this.flightTrackerConfig.URL_PARAMETERS.ROUTE,
      origin: cityPair.origin?.code,
      destination: cityPair.destination?.code,
      date: departureDate,
    });

    if (this.byRouteForm.valid) {
      this.flightTrackerFacade.formSubmitting(true);
      this.flightTrackerFacade.requestFlightStatusByRoute(
        departureDate,
        cityPair.origin.code,
        cityPair.destination.code,
      );
    }
  }

  getSearchResultsByFlight(): void {
    const isFormValid = this.byFlightNumberForm.valid;
    if (isFormValid) {
      this.flightTrackerFacade.formSubmitting(true);
      const { departureDate, flightNumber } = this.byFlightNumberForm.value;
      // remove spaces and starting "b6" characters from flightNumber
      const newFlightNumber = flightNumber
        .replace(/\s/g, '')
        .replace(/^b6/i, '');
      this.setQueryParams({
        by: this.flightTrackerConfig.URL_PARAMETERS.FLIGHT,
        number: newFlightNumber,
        date: departureDate,
      });
      if (
        !isNaN(this.appConfigService.shortPollFlightTrackerDurationInSeconds) &&
        this.appConfigService.shortPollFlightTrackerDurationInSeconds > 0
      ) {
        this.flightTrackerFacade.requestFlightStatusByNumberPoll(
          newFlightNumber,
          departureDate,
          this.appConfigService.shortPollFlightTrackerDurationInSeconds * 1000,
        );
      } else {
        this.flightTrackerFacade.requestFlightStatusByNumber(
          newFlightNumber,
          departureDate,
        );
      }
    } else {
      focusOnErrorField(this.elementRef);
    }
  }

  // Return focus back to "To" field
  returnFocus() {
    this.cityPairSelectorComponent.focusOnDestinationInput();
  }

  setQueryParams(queryParams: Params) {
    this.router.navigate([this.window.location.pathname], {
      queryParams,
    });
  }

  selectTab(selectedTabNumber: number) {
    this.flightTrackerFacade.setSelectedTabId(selectedTabNumber);
    this.showResults = false;
  }

  onByRouteDateChange() {
    this.showResults = false;
  }

  prePopulateBooker(
    queryParams: Params,
    dateOptions: DateOption[],
    originAirportHashMap: TypedDictionary<Airport>,
    destinationAirportHashMap: TypedDictionary<Airport>,
    currentOrigin: Airport,
  ) {
    const origin = originAirportHashMap[currentOrigin.code] ?? null;
    // default to today if date not set or out of range
    const departureDate =
      dateOptions.find(dateOption => dateOption.value === queryParams.date)
        ?.value ?? dateOptions[1].value;
    if (queryParams.by === this.flightTrackerConfig.URL_PARAMETERS.FLIGHT) {
      this.selectTab(1);
      const flightNumber = queryParams.number ?? null;
      this.byFlightNumberForm.setValue({ flightNumber, departureDate });
      if (flightNumber && departureDate) {
        this.getSearchResults(FlightTrackerTabId.byFlight);
      }
    } else if (
      queryParams.by === this.flightTrackerConfig.URL_PARAMETERS.ROUTE
    ) {
      this.selectTab(0);

      const destination =
        destinationAirportHashMap[
          (queryParams.destination ?? '').toUpperCase()
        ] ?? null;
      this.byRouteForm.patchValue({
        cityPair: { origin, destination },
        departureDate,
      });
      if (origin && destination && departureDate) {
        this.getSearchResults(FlightTrackerTabId.byRoute);
      }
    } else {
      this.selectTab(0);
      this.byRouteForm.patchValue({
        cityPair: { origin, destination: null },
      });
    }
  }

  ngOnDestroy() {
    this.flightTrackerService.stopPolling.next(true);
  }
}
